import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import type { Markierung } from '@/api/archive.api';

// ─── Wer ist auf dem Foto? ───────────────────────────────────────────────────
//
// Die Markierungen liegen ÜBER dem Bild, nicht darin. Das Bild ist das
// unveränderte Original, so wie es hochgeladen wurde – kein Neukodieren, kein
// Einbrennen. Die Pfeile und Namen sind eine eigene Ebene aus HTML, die sich
// ein- und ausblenden lässt; gespeichert sind nur Koordinaten relativ zur
// Bildgröße.
//
// Damit die Ebene genau über dem Bild liegt und nicht über dem Rand drumherum,
// wird die tatsächlich gezeichnete Fläche des Bildes nachgemessen
// (object-scale-down lässt links/rechts oder oben/unten Platz).

export type MarkierungsAnzeige = 'namen' | 'punkte' | 'aus';

interface Rahmen {
  left: number;
  top: number;
  width: number;
  height: number;
}

function useBildRahmen(img: HTMLImageElement | null): [Rahmen | null, () => void] {
  const [rahmen, setRahmen] = useState<Rahmen | null>(null);
  const messe = useCallback(() => {
    if (!img) return;
    const cw = img.clientWidth;
    const ch = img.clientHeight;
    // SVGs ohne feste Größe melden keine natürliche Größe – dann die ganze
    // Fläche des Elements nehmen.
    if (!img.naturalWidth || !img.naturalHeight) {
      if (img.complete && cw && ch) setRahmen({ left: img.offsetLeft, top: img.offsetTop, width: cw, height: ch });
      return;
    }
    // object-scale-down: nie größer als das Original, sonst wie contain.
    const scale = Math.min(1, cw / img.naturalWidth, ch / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    setRahmen({ left: img.offsetLeft + (cw - w) / 2, top: img.offsetTop + (ch - h) / 2, width: w, height: h });
  }, [img]);

  useLayoutEffect(() => {
    if (!img) return;
    messe();
    const ro = new ResizeObserver(messe);
    ro.observe(img);
    return () => ro.disconnect();
  }, [img, messe]);

  return [rahmen, messe];
}

const klemme = (v: number) => Math.min(1, Math.max(0, v));

interface Props {
  src: string | undefined;
  alt: string;
  markierungen: Markierung[];
  /** Anzeigename je Markierung (Person aus dem Baum oder freier Name). */
  nameVon: (m: Markierung) => string;
  anzeige: MarkierungsAnzeige;
  bearbeiten: boolean;
  aktivId: string | null;
  onAktiv: (id: string | null) => void;
  onNeu?: (x: number, y: number) => void;
  onVerschiebe?: (id: string, x: number, y: number) => void;
}

export function FotoMarkierungen({
  src,
  alt,
  markierungen,
  nameVon,
  anzeige,
  bearbeiten,
  aktivId,
  onAktiv,
  onNeu,
  onVerschiebe,
}: Props) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [rahmen, messe] = useBildRahmen(img);
  const ebene = useRef<HTMLDivElement>(null);
  const zug = useRef<{ id: string; bewegt: boolean; startX: number; startY: number } | null>(null);

  // Beim Wechsel des Bildes neu messen, sobald es geladen ist.
  useEffect(() => {
    if (img?.complete) messe();
  }, [img, src, messe]);

  const position = (e: { clientX: number; clientY: number }) => {
    const r = ebene.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return null;
    return { x: klemme((e.clientX - r.left) / r.width), y: klemme((e.clientY - r.top) / r.height) };
  };

  const klickAufBild = (e: React.MouseEvent) => {
    if (!bearbeiten || !onNeu) {
      onAktiv(null);
      return;
    }
    const p = position(e);
    if (p) onNeu(p.x, p.y);
  };

  const greife = (e: RPointerEvent, id: string) => {
    e.stopPropagation();
    onAktiv(id);
    if (!bearbeiten) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    zug.current = { id, bewegt: false, startX: e.clientX, startY: e.clientY };
  };

  const ziehe = (e: RPointerEvent) => {
    const z = zug.current;
    if (!z || !onVerschiebe) return;
    // Ein paar Pixel Spiel, damit ein Klick nicht als Verschieben zählt.
    if (!z.bewegt && Math.hypot(e.clientX - z.startX, e.clientY - z.startY) < 4) return;
    z.bewegt = true;
    const p = position(e);
    if (p) onVerschiebe(z.id, p.x, p.y);
  };

  const lasse = () => {
    zug.current = null;
  };

  const zeigeEbene = anzeige !== 'aus' || bearbeiten;

  return (
    <div className="relative h-full w-full">
      <img
        ref={setImg}
        src={src}
        alt={alt}
        onLoad={messe}
        draggable={false}
        className="block h-full w-full select-none object-scale-down"
      />

      {rahmen && zeigeEbene && (
        <div
          ref={ebene}
          className={`absolute ${bearbeiten ? 'cursor-crosshair' : ''}`}
          style={{ left: rahmen.left, top: rahmen.top, width: rahmen.width, height: rahmen.height }}
          onClick={klickAufBild}
        >
          {markierungen.map((m, i) => {
            const aktiv = aktivId === m.id;
            const name = nameVon(m);
            // Beschriftung unterhalb des Punkts, außer ganz unten im Bild.
            const oben = m.y > 0.78;
            // Am Rand nicht über das Bild hinausragen.
            const ausrichtung = m.x < 0.15 ? 'links' : m.x > 0.85 ? 'rechts' : 'mitte';
            const zeigeName = anzeige === 'namen' || aktiv || bearbeiten;
            const verschiebung =
              ausrichtung === 'links' ? '-14px' : ausrichtung === 'rechts' ? 'calc(-100% + 14px)' : '-50%';
            const pfeilLinks = ausrichtung === 'links' ? '14px' : ausrichtung === 'rechts' ? 'calc(100% - 14px)' : '50%';

            return (
              <div
                key={m.id}
                className="absolute"
                style={{ left: `${m.x * 100}%`, top: `${m.y * 100}%`, zIndex: aktiv ? 20 : 10 }}
              >
                {/* Der Punkt, auf den der Pfeil zeigt */}
                <button
                  type="button"
                  onPointerDown={(e) => greife(e, m.id)}
                  onPointerMove={ziehe}
                  onPointerUp={lasse}
                  onPointerCancel={lasse}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => onAktiv(m.id)}
                  onMouseLeave={() => !bearbeiten && onAktiv(null)}
                  className={`absolute flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 touch-none items-center justify-center rounded-full border-2 text-[9px] font-bold shadow-md transition-transform ${
                    aktiv ? 'scale-125 border-white bg-blue-600 text-white' : 'border-white bg-black/55 text-white'
                  } ${bearbeiten ? 'cursor-move' : 'cursor-pointer'}`}
                  aria-label={`${i + 1}: ${name}`}
                  title={name}
                >
                  {i + 1}
                </button>

                {/* Beschriftung mit Pfeil */}
                {zeigeName && (
                  <div
                    className="pointer-events-none absolute"
                    style={{
                      left: 0,
                      top: 0,
                      transform: oben
                        ? `translate(${verschiebung}, calc(-100% - 16px))`
                        : `translate(${verschiebung}, 16px)`,
                    }}
                  >
                    <div
                      className={`relative max-w-[12rem] whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium shadow-lg ${
                        aktiv ? 'bg-blue-600 text-white' : 'bg-white/95 text-slate-800'
                      }`}
                    >
                      <span className="block truncate">{name}</span>
                      <span
                        className={`absolute h-0 w-0 -translate-x-1/2 border-x-[6px] border-x-transparent ${
                          oben
                            ? `top-full border-t-[7px] ${aktiv ? 'border-t-blue-600' : 'border-t-white/95'}`
                            : `bottom-full border-b-[7px] ${aktiv ? 'border-b-blue-600' : 'border-b-white/95'}`
                        }`}
                        style={{ left: pfeilLinks }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
